/**
 * AdminContestEntriesPage — `/admin/contest/entries`.
 *
 * Admin-only moderation surface for the Cutest Pluck contest.
 * Reads `GET /api/admin/contest/entries?status=<filter>` and
 * exposes inline approve / reject via the paired POST endpoints.
 * Reject opens a lightweight inline reason textarea (per-row) so
 * the moderator can drop context without a modal round-trip.
 *
 * Design rules mirror `AdminReviewsPage.jsx`:
 *   - Status filter dropdown only (pending / approved / rejected / all).
 *   - Lean table (thumbnail + dog name + caption + user + consent
 *     timestamp + reason input + approve/reject buttons).
 *   - Reject button disabled until a reason has been typed for
 *     that row (matches the server's `reason_required` gate).
 *
 * No hex colors, no `prefers-color-scheme` — token-only styling.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadCsv } from "@/lib/csvDownload";
import { trackEvent } from "@/lib/analytics";
import { CheckCircle2, XCircle, Loader2, ImageOff, Mail, Download } from "lucide-react";
import AdminContestTabs from "@/components/admin/AdminContestTabs";

function StatusPill({ status }) {
  const style = {
    pending: "bg-pp-surface-2 text-brand-orange",
    approved: "bg-pp-surface-2 text-emerald-700",
    rejected: "bg-pp-surface-2 text-red-700",
  }[status] || "bg-pp-surface-2 text-pp-text";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${style}`}
      data-testid={`admin-contest-status-pill-${status}`}
    >
      {status}
    </span>
  );
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AdminContestEntriesPage() {
  const [statusFilter, setStatusFilter] = useState("pending");
  // Feature A (Feb 2026 launch-blocking Contest A+B batch) —
  // moderator-facing filters. `sort` is session-only per the human
  // dispatch (no localStorage persistence). `includeDemo` and
  // `optedInOnly` mirror the backend query params.
  const [sort, setSort] = useState("date");
  const [includeDemo, setIncludeDemo] = useState(false);
  const [optedInOnly, setOptedInOnly] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Per-row rejection-reason drafts, keyed by entry id.
  const [rejectionDrafts, setRejectionDrafts] = useState({});
  // Per-row action-in-flight guard, keyed by entry id.
  const [inFlight, setInFlight] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        sort,
        include_demo: String(includeDemo),
      });
      const resp = await api.get(
        `/admin/contest/entries?${params.toString()}`,
      );
      setEntries(resp.data?.entries || []);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load queue.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sort, includeDemo]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleApprove(entry) {
    setInFlight((s) => ({ ...s, [entry.id]: "approve" }));
    try {
      await api.post(`/admin/contest/entries/${entry.id}/approve`);
      toast.success(`Approved ${entry.dog_name}`);
      // Commit B — §4.1 funnel end. Fired from admin FE on
      // approval-action success (NOT from the locked backend
      // approval handler in `contest_entries.py`). Rationale
      // captured in `admin_analytics_dashboard_scope.md` §4.1 —
      // frontend emission keeps analytics wiring out of SHA-locked
      // backend surfaces.
      trackEvent("contest.entry_approved", { entry_id: entry.id });
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Approve failed.",
      );
    } finally {
      setInFlight((s) => {
        const { [entry.id]: _, ...rest } = s;
        return rest;
      });
    }
  }

  async function handleReject(entry) {
    const reason = (rejectionDrafts[entry.id] || "").trim();
    if (!reason) return;
    setInFlight((s) => ({ ...s, [entry.id]: "reject" }));
    try {
      await api.post(`/admin/contest/entries/${entry.id}/reject`, { reason });
      toast.success(`Rejected ${entry.dog_name}`);
      setRejectionDrafts((s) => {
        const { [entry.id]: _, ...rest } = s;
        return rest;
      });
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Reject failed.",
      );
    } finally {
      setInFlight((s) => {
        const { [entry.id]: _, ...rest } = s;
        return rest;
      });
    }
  }

  async function handleExportCsv() {
    // Feature A — respects the CURRENT filter state (status,
    // include_demo, sort, opted_in_only). Server sets the
    // Content-Disposition filename; we pass through so the download
    // uses `contest_entries_<YYYY-MM-DD>.csv`.
    setExporting(true);
    try {
      await downloadCsv("/admin/contest/entries/export.csv", {
        params: {
          status: statusFilter,
          include_demo: includeDemo,
          sort,
          opted_in_only: optedInOnly,
        },
      });
      toast.success("CSV export downloaded.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Export failed.",
      );
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-contest-entries-page">
      <AdminContestTabs />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-pp-text">
            Cutest Pluck moderation queue
          </h1>
          <p className="text-sm text-muted-foreground">
            Approve or reject contest entries.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-semibold text-pp-text">Status</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-40 rounded-full"
              data-testid="admin-contest-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending" data-testid="admin-contest-filter-pending">
                Pending
              </SelectItem>
              <SelectItem value="approved" data-testid="admin-contest-filter-approved">
                Approved
              </SelectItem>
              <SelectItem value="rejected" data-testid="admin-contest-filter-rejected">
                Rejected
              </SelectItem>
              <SelectItem value="all" data-testid="admin-contest-filter-all">
                All
              </SelectItem>
            </SelectContent>
          </Select>

          <label className="text-sm font-semibold text-pp-text">Sort</label>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger
              className="w-56 rounded-full"
              data-testid="admin-contest-sort-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date" data-testid="admin-contest-sort-date">
                Date (newest first)
              </SelectItem>
              <SelectItem value="votes" data-testid="admin-contest-sort-votes">
                Votes (highest first)
              </SelectItem>
            </SelectContent>
          </Select>

          <label
            className="flex items-center gap-2 text-sm text-pp-text"
            htmlFor="admin-contest-include-demo"
          >
            <Checkbox
              id="admin-contest-include-demo"
              checked={includeDemo}
              onCheckedChange={(v) => setIncludeDemo(v === true)}
              data-testid="admin-contest-include-demo-checkbox"
            />
            Include demo entries
          </label>

          <label
            className="flex items-center gap-2 text-sm text-pp-text"
            htmlFor="admin-contest-opted-in-only"
          >
            <Checkbox
              id="admin-contest-opted-in-only"
              checked={optedInOnly}
              onCheckedChange={(v) => setOptedInOnly(v === true)}
              data-testid="admin-contest-opted-in-only-checkbox"
            />
            Opted-in only (export)
          </label>

          <Button
            type="button"
            onClick={handleExportCsv}
            disabled={exporting}
            variant="secondary"
            className="rounded-full"
            data-testid="admin-contest-export-csv-button"
          >
            {exporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export CSV
          </Button>
        </div>
      </div>

      {loading && (
        <div
          className="flex items-center justify-center rounded-2xl border border-border bg-pp-surface p-10"
          data-testid="admin-contest-loading"
        >
          <Loader2 className="h-5 w-5 animate-spin text-brand-orange" />
        </div>
      )}

      {!loading && error && (
        <div
          className="rounded-2xl border border-border bg-pp-surface p-6 text-sm text-red-700"
          data-testid="admin-contest-error"
        >
          {error}
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <div
          className="rounded-2xl border border-border bg-pp-surface p-10 text-center text-sm text-muted-foreground"
          data-testid="admin-contest-empty"
        >
          No entries match the current filter.
        </div>
      )}

      {!loading && !error && entries.length > 0 && (
        <div
          className="space-y-3"
          data-testid="admin-contest-entries-list"
        >
          {entries.map((e) => {
            const flight = inFlight[e.id];
            const rejectDraft = (rejectionDrafts[e.id] || "").trim();
            return (
              <div
                key={e.id}
                className="flex flex-col gap-4 rounded-2xl border border-border bg-pp-surface p-5 sm:flex-row"
                data-testid={`admin-contest-row-${e.id}`}
              >
                <div className="flex-shrink-0">
                  {e.photo_url ? (
                    <img
                      src={e.photo_url}
                      alt={e.dog_name}
                      className="h-24 w-24 rounded-2xl object-cover"
                      data-testid={`admin-contest-thumb-${e.id}`}
                    />
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-pp-surface-2 text-muted-foreground">
                      <ImageOff className="h-6 w-6" />
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3
                      className="font-heading text-lg font-bold text-pp-text"
                      data-testid={`admin-contest-dog-name-${e.id}`}
                    >
                      {e.dog_name}
                    </h3>
                    <StatusPill status={e.status} />
                  </div>
                  {e.caption && (
                    <p
                      className="text-sm text-pp-text"
                      data-testid={`admin-contest-caption-${e.id}`}
                    >
                      {e.caption}
                    </p>
                  )}
                  <div className="text-xs text-muted-foreground">
                    User:{" "}
                    <span data-testid={`admin-contest-user-${e.id}`}>
                      {e.owner_email || e.owner_name || e.user_id}
                    </span>
                    {" · "}
                    Consented: {formatDateTime(e.consent_recorded_at)}
                    {" · "}
                    <span data-testid={`admin-contest-vote-count-${e.id}`}>
                      {(e.vote_count ?? 0)} vote{e.vote_count === 1 ? "" : "s"}
                    </span>
                    {e.marketing_opt_in && (
                      <>
                        {" · "}
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-pp-surface-2 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-orange"
                          title="Entrant opted in to marketing email"
                          data-testid={`admin-contest-marketing-optin-${e.id}`}
                        >
                          <Mail className="h-3 w-3" />
                          Marketing opt-in
                        </span>
                      </>
                    )}
                  </div>
                  {e.status === "rejected" && e.status_reason && (
                    <div className="text-xs text-muted-foreground">
                      Reason:{" "}
                      <span data-testid={`admin-contest-reason-${e.id}`}>
                        {e.status_reason}
                      </span>
                    </div>
                  )}
                  {e.status === "pending" && (
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                      <Button
                        type="button"
                        onClick={() => handleApprove(e)}
                        disabled={flight != null}
                        className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid={`admin-contest-approve-${e.id}`}
                      >
                        {flight === "approve" ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                        )}
                        Approve
                      </Button>
                      <div className="flex flex-1 flex-col gap-2 sm:flex-row">
                        <Textarea
                          value={rejectionDrafts[e.id] || ""}
                          onChange={(ev) =>
                            setRejectionDrafts((s) => ({
                              ...s,
                              [e.id]: ev.target.value,
                            }))
                          }
                          placeholder="Rejection reason…"
                          className="min-h-[40px] flex-1 rounded-2xl border-border bg-pp-surface-2 px-3 py-2 text-sm"
                          data-testid={`admin-contest-reject-reason-${e.id}`}
                        />
                        <Button
                          type="button"
                          onClick={() => handleReject(e)}
                          disabled={!rejectDraft || flight != null}
                          variant="secondary"
                          className="rounded-full"
                          data-testid={`admin-contest-reject-${e.id}`}
                        >
                          {flight === "reject" ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <XCircle className="mr-2 h-4 w-4" />
                          )}
                          Reject
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
